import { Component, EventEmitter, Input, OnInit, Output, ViewChild, signal } from '@angular/core';
import { FilterSidebarComponent } from '@app/@shared/components/filter-sidebar/filter-sidebar.component';
import { ContentNavigatorTab, TabGroup } from '@app/@shared/models/content-navigator/content-navigator.model';
import { PaginationParameters } from '@app/@shared/models/pagination';
import { AnalysisFilter, Filters, FiltersChangedEvent } from '@app/@shared/models/reverse-engineering/analysis.models';
import { SelectOption } from '@app/@shared/models/shared.models';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { NavigationModules } from '@app/@shared/models/navigation/navigation.models';
import {ActivatedRoute} from "@angular/router";

/**
 * Use the showFilters input as false if the filter sidebar is not required
 */
@Component({
    selector: 'app-tabbed-analysis-list',
    templateUrl: './tabbed-analysis-list.component.html',
    styleUrls: ['./tabbed-analysis-list.component.scss'],
    standalone: false
})
export class TabbedAnalysisListComponent implements OnInit {
  @Input() showFilters = true;
  @Input() showSort = true;
  @Input() showResultsText = true;
  @Input() loading = false;
  @Input() count: number;
  @Input() pageSize: number;
  @Input() page: number;
  @Input() pageSizes: number[];
  @Input() set tabGroups(t: TabGroup[]) {
    this._tabGroups = t;
    this.hasMoreThanOneTab();
  };
  get tabGroups(): TabGroup[] {
    return this._tabGroups;
  }
  @Input() activeTab: ContentNavigatorTab;
  @Input() activeTabGroup: TabGroup;
  @Input() filtersCollapsed = false;
  @Input() set filters(incomingFilters: AnalysisFilter[]) {
    this._filters = incomingFilters;
    this.filtersApplied = this._filters?.find((f) => f.options.filter((o) => o.selected).length > 0) !== undefined;
  }
  get filters(): AnalysisFilter[] {
    return this._filters;
  }
  @Input() set searchTerm(searchTerm: string) {
    this._searchTerm = searchTerm?.replace(/,/g, '" OR "');
  }

  @Input() breadcrumbString: string;
  @Input() showPagination = true;
  @Input() sortOptions: SelectOption<string>[];
  @Input() defaultSort: SelectOption<string>;
  @Input() showHighlightToggle = true;
  @Input() showHighlights = false;
  @Input() searchModule = '';

  @Input() selectedFilters: Filters = {};
  @Input() isMobile$: Observable<boolean>;
  @Input() currentDateRange?: { from: Date; to: Date };
  @Input() useNewSearchHeader = false;

  @Output() changePaginationOptions = new EventEmitter<PaginationParameters>();
  @Output() clickTab = new EventEmitter<any>();
  @Output() toggleFilters = new EventEmitter<boolean>();
  @Output() changeFilters = new EventEmitter<FiltersChangedEvent>();

  @Output() dateRangeChanged = new EventEmitter<{ from: Date; to: Date }>();

  @ViewChild('filtersBar') filtersBar: FilterSidebarComponent;

  filterSidebarToggleSignal = signal(true);

  currentSelectedSort: SelectOption<string> | null = null;

  filtersApplied = false;
  _searchTerm: string;
  maxAmountResultsExceededMessage =
    'Your search query returned more than 10000 results.\nIf you don\'t see what you\'re looking for, try adjusting\nyour search terms or using filters.';
  iconClassInfo = 'icon-info';
  moreThanOneTab: boolean;
  readonly search = NavigationModules.SEARCH;

  private _filters: AnalysisFilter[];
  private _tabGroups: TabGroup[];

  constructor(private readonly route: ActivatedRoute) {}

  ngOnInit() {
    this.currentSelectedSort = this.defaultSort;
    this.filtersApplied = this.filters?.find((f) => f.options.filter((o) => o.selected).length > 0) !== undefined;
    this.filters = this.filters?.map((component) => {
      if (component.field === 'analysisYear') {
        component.options = component.options.filter((option) => option.label !== 'In Progress');
      }
      return component;
    });

    this.route.queryParams.subscribe((params) => {
      const sortFromUrl = params['sort'] || '';
      if (sortFromUrl) {
        const found = this.sortOptions.find(o => o.value === sortFromUrl);
        this.currentSelectedSort = found ?? null;
      }
    });
  }

  getFilteredTabs(tabs: Array<ContentNavigatorTab>): Observable<Array<ContentNavigatorTab>> {
    return of(tabs);
  }

  getShowedTabs(tabs: Array<ContentNavigatorTab>): Observable<Array<ContentNavigatorTab>> {
    return this.getFilteredTabs(tabs).pipe(
      map((filteredTabs) => {
        return filteredTabs.filter((t) => !t.hide);
      })
    );
  }

  /**
   * Emits an event when a new tab is clicked
   * @param tab the newly selected tab
   */
  onClickTab(tab: ContentNavigatorTab, tabGroupId: string) {
    this.clickTab.emit({ tab, tabGroupId });
  }

  /**
   * Emits an event when the pagination options changes
   * @param options the new pagination options
   */
  onChangePaginationOptions(options: PaginationParameters | SelectOption<string>) {
    if (options && 'value' in options && 'label' in options) {
      this.currentSelectedSort = options as SelectOption<string>;

      const paginationParams = new PaginationParameters(
        this.pageSize,
        1,
        options.value,
        { showHighlights: this.showHighlights }
      );
      this.changePaginationOptions.emit(paginationParams);
    } else {
      this.changePaginationOptions.emit(options as PaginationParameters);
    }
  }

  /**
   * Emits an event when the button to toggle the sidebar is clicked
   */
  onToggleFilters() {
    this.filtersCollapsed = !this.filtersCollapsed;
    this.filterSidebarToggleSignal.set(!this.filtersCollapsed);
    this.toggleFilters.emit(this.filtersCollapsed);
  }

  /**
   * Emits an event if the selected filters change
   * @param e the event
   */
  onFiltersChanged(e: FiltersChangedEvent) {
    this.filtersApplied = e.applied.length > 0;
    this.changeFilters.emit(e);
  }

  /**
   * Clears all selected filters
   */
  clearFilters() {
    this.filtersBar.clearSelectedFilters();
  }

  /**
   * Handles date range changes from the search results header
   * @param dateRange the selected date range
   */
  onDateRangeChanged(dateRange: { from: Date; to: Date }) {
    this.currentDateRange = dateRange;
    this.dateRangeChanged.emit(dateRange);
  }


  private hasMoreThanOneTab() {
    this.moreThanOneTab = this.tabGroups.length > 1 || this.tabGroups[0].tabs.length > 1;
  }
}
